import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Github, Shield, Code, GitBranch } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { GitHubSettings } from '@/components/features/github/GitHubSettings';

export function GitHubSettingsPage() {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back link */}
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Settings
      </button>

      {/* Page Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gray-900 dark:bg-white rounded-xl flex items-center justify-center">
          <Github className="w-6 h-6 text-white dark:text-gray-900" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">GitHub Integration</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Connect your GitHub account to sync your projects
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Main Connection Card */}
        <GitHubSettings />

        {/* Features Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-gray-500" />
              What you can do with GitHub
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <FeatureItem
                icon={<GitBranch className="w-5 h-5" />}
                title="Create Repositories"
                description="Automatically create new GitHub repos for your Spark projects"
              />
              <FeatureItem
                icon={<Code className="w-5 h-5" />}
                title="Push Code"
                description="Sync your project code to GitHub with a single click"
              />
              <FeatureItem
                icon={<Github className="w-5 h-5" />}
                title="Link Existing Repos"
                description="Connect your projects to existing GitHub repositories"
              />
              <FeatureItem
                icon={<Shield className="w-5 h-5" />}
                title="Version Control"
                description="Keep track of all changes with full commit history"
              />
            </div>
          </CardContent>
        </Card>

        {/* Permissions Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-500" />
              Permissions & Privacy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <p>When you connect GitHub, Spark requests the following permissions:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>repo</strong> - Full access to your repositories (required to create, push, and pull code)</li>
                <li><strong>read:user</strong> - Read your GitHub profile info (username, avatar)</li>
              </ul>
              <p className="mt-4">
                You can revoke access at any time from your{' '}
                <a
                  href="https://github.com/settings/applications"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                  GitHub settings
                </a>.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FeatureItem({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-700/50">
      <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-gray-900 dark:text-white">{title}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
      </div>
    </div>
  );
}
